/**
 * Carga de clientes, proyectos, reportes visibles, pendientes y notificaciones.
 */
import React, { createContext, useState, useCallback, useEffect, useContext } from 'react';
import { useAuth } from './AuthContext';

const DataContext = createContext();

export const useData = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
    const { user, userRole, userPermissions, impersonation, fetchWithAuth, API_URL, setLoading: setAuthLoading } = useAuth();
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [dashboards, setDashboards] = useState([]);
    const [pendingApprovalReports, setPendingApprovalReports] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);

    const fetchClients = useCallback(async () => {
        try {
            const res = await fetchWithAuth(`${API_URL}/admin/clients`);
            const data = await res.json();
            setClients(data.success ? data.clients || [] : []);
        } catch (err) {
            console.error("Failed to fetch clients:", err);
            setClients([]);
        }
    }, [fetchWithAuth, API_URL]);

    const fetchProjects = useCallback(async () => {
        try {
            const res = await fetchWithAuth(`${API_URL}/admin/projects`);
            const data = await res.json();
            setProjects(data.success ? data.projects || [] : []);
        } catch (err) {
            console.error("Failed to fetch projects:", err);
            setProjects([]);
        }
    }, [fetchWithAuth, API_URL]);

    const fetchDashboards = useCallback(async (uid, options = {}) => {
        if (!options.background) setAuthLoading(true);
        const url = userPermissions.reports_view_all
            ? `${API_URL}/admin/reportes-maestros`
            : `${API_URL}/dashboards/${uid}`;
        try {
            const res = await fetchWithAuth(url);
            const data = await res.json();
            const dashboardsData = data.reports || data.reportes || [];
            setDashboards(dashboardsData);
        } catch (error) {
            console.error(`Error fetching dashboards:`, error);
            setDashboards([]);
        } finally {
            if (!options.background) setAuthLoading(false);
        }
    }, [fetchWithAuth, API_URL, userPermissions.reports_view_all, setAuthLoading]);

    const fetchPendingApprovalReports = useCallback(async (uid) => {
        try {
            const res = await fetchWithAuth(`${API_URL}/dashboards/${uid}/pending-approval`);
            const data = await res.json();
            setPendingApprovalReports(data.success ? data.reports || [] : []);
        } catch (err) {
            console.error("Failed to fetch pending approval reports:", err);
            setPendingApprovalReports([]);
        }
    }, [fetchWithAuth, API_URL]);

    const fetchNotifications = useCallback(async (uid) => {
        if (!uid) {
            setNotifications([]);
            return;
        }
        try {
            const res = await fetchWithAuth(`${API_URL}/notifications/user/${uid}`);
            const data = await res.json();
            setNotifications(data.success ? data.notifications || [] : []);
        } catch (err) {
            console.error("DataContext: Fetch notifications failed:", err);
            setNotifications([]);
        }
    }, [fetchWithAuth, API_URL]);

    // Efecto para calcular el contador de no leídas cada vez que las notificaciones cambian
    useEffect(() => {
        const unread = notifications.filter(n => !n.read).length;
        setUnreadNotificationsCount(unread);
    }, [notifications]);

    const refreshAllData = useCallback(() => {
        if (user) {
            const uidToFetch = impersonation ? impersonation.uid : user.uid;
            fetchDashboards(uidToFetch, { background: true });
            fetchClients();
            fetchProjects();
            fetchNotifications(uidToFetch);
            fetchPendingApprovalReports(uidToFetch);
        }
    }, [user, impersonation, fetchDashboards, fetchClients, fetchProjects, fetchNotifications, fetchPendingApprovalReports]);

    useEffect(() => {
        if (user) {
            refreshAllData();
        } else {
            setDashboards([]); setClients([]); setProjects([]); setPendingApprovalReports([]); setNotifications([]); setAuthLoading(false);
        }
    }, [user, userRole, impersonation, refreshAllData, setAuthLoading]);

    const value = {
        clients, projects, dashboards, setDashboards, pendingApprovalReports, refreshAllData,
        notifications, setNotifications, unreadNotificationsCount
    };

    return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};